// ~src/utils/tender/IsTenderClosed.ts

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';

export type TenderLikeWithLifetime = {
  /** registration.state */
  state?: number | null;
  /**
   * registration.deadline_at
   * Unix timestamp in seconds (API format).
   * If set and the moment has passed => closed.
   */
  deadline_at?: number | null;
};

/**
 * isTenderClosed(registration)
 *
 * MVP lifetime rules:
 * - CLOSED if state >= 6
 * - CLOSED if deadline_at is set (>0) and deadline_at <= now (unix sec)
 * Otherwise AVAILABLE.
 *
 * Usage:
 *   const tenderClosed = isTenderClosed(tender);
 *   <Button disabled={tenderClosed} />
 */
export const isTenderClosed = (
  registration: TenderLikeWithLifetime | null | undefined,
  nowUnixSec: number = Math.floor(Date.now() / 1000)
): boolean => {
  if (!registration) return false;

  const state =
    typeof registration.state === 'number' ? registration.state : null;

  const deadlineAt =
    typeof registration.deadline_at === 'number'
      ? registration.deadline_at
      : null;

  // 1) Tender closed by state
  if (state !== null && state >= 6) return true;

  // 2) Tender closed by deadline (only if deadline_at is set)
  if (deadlineAt !== null && deadlineAt > 0 && deadlineAt <= nowUnixSec)
    return true;

  return false;
};

type TenderRegData = {
  state?: number;
  deadline_at?: number;
  execution_begin?: number;
};

type VendorSubHeaderResult = {
  text: string;
  color: string;
} | null;

/**
 * Vendor (Shares side) Contracts / Offers sub-header logic
 */
export const useTenderVendorSubHeader = (
  tenderRegData?: TenderRegData
): VendorSubHeaderResult => {
  const intl = useIntl();

  return useMemo(() => {
    if (!tenderRegData) return null;

    const state = Number(tenderRegData.state ?? 0);
    const tenderClosed = isTenderClosed(tenderRegData);

    // --- dates ---
    const deadlineAtText =
      typeof tenderRegData.deadline_at === 'number'
        ? moment.unix(tenderRegData.deadline_at).format('LLL')
        : '';

    const executionBeginText =
      typeof tenderRegData.execution_begin === 'number'
        ? moment.unix(tenderRegData.execution_begin).format('LLL')
        : '';

    // --- pick string id by state ---
    let id:
      | 'tab-vendors-contract-sub-description-draft'
      | 'tab-vendors-contract-sub-description-active'
      | 'tab-vendors-contract-sub-description-closed'
      | 'tab-vendors-contract-sub-description-removed';

    if (state >= 9) {
      id = 'tab-vendors-contract-sub-description-removed';
    } else if (state >= 6) {
      id = 'tab-vendors-contract-sub-description-closed';
    } else if (state >= 3) {
      id = 'tab-vendors-contract-sub-description-active';
    } else {
      // Draft – not used yet, but implemented
      id = 'tab-vendors-contract-sub-description-draft';
    }

    // --- format text ---
    const raw = intl.formatMessage({ id });

    const dateText =
      id === 'tab-vendors-contract-sub-description-draft'
        ? executionBeginText || deadlineAtText
        : deadlineAtText;

    const text = raw
      .replace(/<&date>/g, dateText)
      .replace(/<\&date>/g, dateText);

    // --- color logic ---
    let color = 'grey';

    if (state >= 9) {
      color = '#a8071a'; // removed → red
    } else if (state >= 6) {
      color = '#ad4e00'; // closed → orange/red
    } else if (state >= 3 && tenderClosed) {
      color = '#ad4e00'; // deadline passed
    }

    return { text, color };
  }, [tenderRegData, intl]);
};

export default isTenderClosed;
